import { z } from 'zod';
import { defaultCompetencyRequirementMappings, defaultCompetencyRoleForms } from '../catalog/settings.seed.js';
export const competencyRoleFormsSchema = z.object({
    jobProfileKey: z.string().trim().min(1).max(200),
    initialFormId: z.string().trim().min(1).max(200),
    ongoingFormId: z.string().trim().min(1).max(200),
}).strict();
export const competencyRoleMappingsSchema = z.array(competencyRoleFormsSchema).max(100).superRefine((rows, context) => {
    const seen = new Set();
    rows.forEach((row, index) => {
        if (seen.has(row.jobProfileKey))
            context.addIssue({ code: 'custom', path: [index, 'jobProfileKey'], message: 'Each role needs one competency mapping' });
        seen.add(row.jobProfileKey);
    });
});
export const competencyRequirementMappingSchema = z.object({
    requirementKey: z.string().trim().min(1).max(200),
    assessmentType: z.enum(['initial', 'ongoing']),
    /** Null means all of this person's roles; a list narrows a role-specific requirement. */
    jobProfileKeys: z.array(z.string().trim().min(1).max(200)).max(100).nullable().default(null),
}).strict();
export const competencyRequirementMappingsSchema = z.array(competencyRequirementMappingSchema).max(100).superRefine((rows, context) => {
    const seen = new Set();
    rows.forEach((row, index) => {
        if (seen.has(row.requirementKey))
            context.addIssue({ code: 'custom', path: [index, 'requirementKey'], message: 'Each requirement needs one competency mapping' });
        seen.add(row.requirementKey);
    });
});
export function competencyFormsFor(requirementKey, jobProfileKeys, mappings = defaultCompetencyRoleForms, requirements = defaultCompetencyRequirementMappings) {
    const requirement = requirements.find((row) => row.requirementKey === requirementKey);
    // Removing a baseline mapping is a configuration gap, not permission to bypass its evidence.
    if (requirement === undefined)
        return defaultCompetencyRequirementMappings.some((row) => row.requirementKey === requirementKey) ? [] : null;
    const { assessmentType } = requirement;
    const roles = requirement.jobProfileKeys === null ? jobProfileKeys
        : jobProfileKeys.filter((key) => requirement.jobProfileKeys?.includes(key));
    // A partly mapped multi-role person must not be cleared using only the mapped role.
    if (roles.some((key) => !mappings.some((row) => row.jobProfileKey === key)))
        return [];
    return [...new Set(roles)].flatMap((jobProfileKey) => {
        const mapped = mappings.find((row) => row.jobProfileKey === jobProfileKey);
        return mapped === undefined ? [] : [{ jobProfileKey, assessmentType, formId: assessmentType === 'initial' ? mapped.initialFormId : mapped.ongoingFormId }];
    });
}
export function competencyCoverageMatches(expected, recorded) {
    return expected.length > 0 && expected.every((form) => recorded.some((entry) => entry.jobProfileKey === form.jobProfileKey && entry.assessmentType === form.assessmentType && entry.formId === form.formId &&
        entry.evaluator.trim() !== '' && entry.evaluatorTitle.trim() !== '' &&
        // A committed file, or an external link (ADR-009 Q4). An upload id alone is not evidence: it
        // becomes `evidenceFileId` only once the file is committed (#188).
        (entry.evidenceFileId !== undefined || (entry.evidenceUrl?.startsWith('https://') ?? false))));
}
//# sourceMappingURL=competency.js.map